import { LoadingSpinner } from "@/components/loading-spinner";
import { Button } from "@/components/ui/button";
import { useAssessmentStore } from "@/stores/assessment";
import { Alert, Fade, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Modal } from "../../../../components";
import { useInputText } from "../../../../hooks";
import services from "../../../../services";
import { useData } from "../use-data";

interface ModalUpdateProps {
  open: boolean;
  onClose: () => void;
}

export default function ModalUpdate({ open, onClose }: ModalUpdateProps) {
  const { refetch } = useData();
  const { selectedItem } = useAssessmentStore();
  const initialValues = { name: "", duration: "", description: "" };
  const input = useInputText(initialValues);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preencher campos ao abrir o modal
  useEffect(() => {
    if (open && selectedItem) {
      input.updateInitial("name", selectedItem.name || "");
      input.updateInitial("duration", selectedItem.duration?.toString() || "");
      input.updateInitial("description", selectedItem.description || "");
    }
    if (!open) {
      input.clear();
      setError(null);
      setLoading(false);
    }
  }, [open, selectedItem]);

  const resetStates = useCallback(() => {
    input.clear();
    setError(null);
    setLoading(false);
  }, [input]);

  const closeModal = useCallback(() => {
    resetStates();
    onClose();
    refetch();
  }, [onClose, refetch, resetStates]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (!selectedItem) {
        setError("Nenhuma avaliação selecionada para atualizar.");
        setLoading(false);
        return;
      }
      const { name, duration, description } = input;
      if (!name || name.trim() === "") {
        setError("O nome da avaliação é obrigatório.");
        setLoading(false);
        return;
      }
      const data: any = {
        name: name.trim(),
        id: selectedItem.id,
      };
      if (duration !== "" && !isNaN(Number(duration))) {
        data.duration = Number(duration);
      }
      if (description && description.trim() !== "") {
        data.description = description.trim();
      }
      await services.api.put("/assessments/", data);
      toast.success("Avaliação atualizada com sucesso");
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Erro ao atualizar avaliação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      className="w-full max-w-[90%] md:max-w-[80%] lg:max-w-[520px] max-h-[95%]"
      head={<h2 className="text-xl font-bold">Atualizar Avaliação</h2>}
    >
      <Fade in={open}>
        {loading ? (
          <div className="flex items-center justify-center h-full min-h-[300px] w-full flex-col gap-4">
            <LoadingSpinner />
            <p className="text-sm text-gray-500">Atualizando avaliação...</p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="grid gap-6 h-full w-full p-6"
          >
            <TextField
              required
              label="Nome da Avaliação"
              name="name"
              value={input.name}
              onChange={input.onChange}
              onBlur={input.onBlur}
              error={!!input.errors.name}
              helperText={input.errors.name}
              disabled={loading}
              fullWidth
              size="small"
            />
            <TextField
              label="Duração da Aula (minutos)"
              name="duration"
              type="number"
              value={input.duration}
              onChange={input.onChange}
              onBlur={input.onBlur}
              disabled={loading}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
              helperText="Opcional"
            />
            <TextField
              label="Descrição"
              name="description"
              value={input.description}
              onChange={input.onChange}
              onBlur={input.onBlur}
              disabled={loading}
              fullWidth
              size="small"
              multiline
              minRows={3}
              maxRows={6}
              helperText="Opcional"
            />
            {error && (
              <Alert severity="error" className="text-sm">
                {error}
              </Alert>
            )}
            <div className="flex items-center justify-end gap-3 mt-4 border-t pt-4">
              <Button
                disabled={loading}
                variant="outline"
                type="reset"
                onClick={closeModal}
                className="min-w-[120px] py-3 h-fit"
                size="sm"
              >
                Cancelar
              </Button>
              <Button
                disabled={loading}
                type="submit"
                className="min-w-[120px] py-3 h-fit"
                size="sm"
              >
                Atualizar Avaliação
              </Button>
            </div>
          </form>
        )}
      </Fade>
    </Modal>
  );
}
